import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { StatusBadge } from "@/components/common/Form";
import { useStore } from "@/context/StoreContext";
import { LoadingState } from "@/components/common/States";
import {
  Package, ShoppingCart, IndianRupee, Star, TrendingUp, FileText, Wallet, Boxes,
} from "lucide-react";

const STATS = [
  [IndianRupee, "Total Sales", "₹0", "bg-emerald-50 text-emerald-700"],
  [ShoppingCart, "Orders", "0", "bg-blue-50 text-blue-700"],
  [Package, "Products", "0", "bg-amber-50 text-amber-700"],
  [Star, "Store Rating", "—", "bg-purple-50 text-purple-700"],
];

const MODULES = [
  [Package, "Product Management", "Add, edit and organize your listings"],
  [Boxes, "Inventory", "Track stock levels & variants"],
  [IndianRupee, "Pricing & B2B", "Retail, wholesale & tier pricing"],
  [ShoppingCart, "Orders", "Fulfil and track customer orders"],
  [FileText, "Incoming Quotes", "Respond to B2B RFQs"],
  [Wallet, "Payouts & Settlements", "Commission & settlement reports"],
  [TrendingUp, "Analytics", "Sales performance insights"],
];

export default function VendorDashboard() {
  const { user } = useStore();
  const [app, setApp] = useState(undefined);
  useEffect(() => {
    let cancelled = false;
    if (user?.role === "vendor") api.get("/vendor/me").then((r) => { if (!cancelled) setApp(r.data.vendor); }).catch(() => { if (!cancelled) setApp(null); });
    return () => { cancelled = true; };
  }, [user]);
  if (user === null) return <LoadingState />;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {user.role === "vendor" && app !== undefined && (
        <div data-testid="vendor-application-banner" className="mb-6 rounded-xl border border-border bg-card p-4 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px]"><div className="font-semibold text-stone-900">Vendor application</div><div className="text-sm text-stone-500">{app ? `${app.business_name} · KYC ${app.verification_status.replace(/_/g, " ").toLowerCase()}` : "Complete onboarding to get your store verified."}</div></div>
          <StatusBadge value={app ? app.status : "DRAFT"} testId="vendor-dashboard-status" />
          {app?.status === "APPROVED" && <><Link to="/vendor/products" data-testid="vendor-go-products" className="rounded-full bg-primary text-white text-sm font-semibold px-4 py-2">My Products</Link><Link to="/vendor/inventory" data-testid="vendor-go-inventory" className="rounded-full border border-border text-sm font-semibold px-4 py-2">Inventory</Link><Link to="/vendor/rfqs" data-testid="vendor-go-rfqs" className="rounded-full border border-border text-sm font-semibold px-4 py-2">Wholesale RFQs</Link></>}
          <Link to="/vendor/onboarding" data-testid="vendor-go-onboarding" className="rounded-full bg-secondary text-white text-sm font-semibold px-4 py-2">{app ? "View application" : "Start onboarding"}</Link>
        </div>
      )}
      <div className="rounded-2xl bg-[#1F1610] text-white p-6 sm:p-8 mb-6">
        <span className="eyebrow text-primary">Vendor Dashboard</span>
        <h1 className="mt-1 text-2xl font-serif font-bold">Welcome, {user && user !== false ? user.name : "Vendor"}</h1>
        <p className="text-sm text-stone-300 mt-1">Your storefront goes live once your account is verified.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {STATS.map(([Icon, label, value, cls]) => (
          <div key={label} className="rounded-xl border border-border bg-card p-5">
            <div className={`h-10 w-10 rounded-lg grid place-items-center mb-3 ${cls}`}><Icon size={20} /></div>
            <div className="text-2xl font-bold text-stone-900">{value}</div>
            <div className="text-sm text-stone-500">{label}</div>
          </div>
        ))}
      </div>

      <h2 className="font-serif font-bold text-xl text-stone-900 mb-4">Your Tools</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {MODULES.map(([Icon, t, d]) => (
          <div key={t} className="rounded-xl border border-border bg-card p-5 flex items-start gap-3">
            <div className="h-11 w-11 rounded-lg bg-accent text-secondary grid place-items-center shrink-0"><Icon size={20} /></div>
            <div>
              <h3 className="font-semibold text-stone-900">{t}</h3>
              <p className="text-sm text-stone-500">{d}</p>
              <span className="mt-1 inline-block text-[11px] font-medium text-secondary bg-secondary/5 rounded-full px-2 py-0.5">Activating soon</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
